import React, { useState, useEffect } from 'react';
import './IngredientsManagement.css';
import { 
  getIngredients, 
  addIngredient, 
  updateIngredient, 
  deleteIngredient,
  getVendors
} from '../services/IngredientsService';

const IngredientsManagement = () => {
  const [ingredients, setIngredients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [supplierNames, setSupplierNames] = useState({});
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [newIngredient, setNewIngredient] = useState({
    code: '',
    tradeName: '',
    vendorId: '',
    purpose: '',
    ingredientName: '',
    casNumber: '',
    percentInAdditive: 1, // Stored as fraction (0-1), 1 = 100%
    percentInHFJob: 0,
    massIngredient: 0,
    units: 'gal',
    specificGravity: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('code');
  const [sortDirection, setSortDirection] = useState('asc');

  // Load ingredients and vendors on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [ingredientsData, vendorsData] = await Promise.all([
          getIngredients(),
          getVendors()
        ]);
        setIngredients(ingredientsData);
        setVendors(vendorsData);
        
        // Create a mapping of vendor IDs to company names
        const supplierNamesMap = {};
        for (const vendor of vendorsData) {
          supplierNamesMap[vendor.id] = vendor.company;
        }
        setSupplierNames(supplierNamesMap);
        
        setError('');
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load ingredients or vendors. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);
  
  // Get company name for a supplier (which might be a vendor ID)
  const getSupplierDisplayName = (supplier) => {
    // If supplier is a number or a string that can be parsed as a number, it might be a vendor ID
    if (!isNaN(supplier)) {
      const vendorId = parseInt(supplier);
      return supplierNames[vendorId] || supplier;
    }
    return supplier;
  };

  // Handle input change for new ingredient form
  const handleNewIngredientChange = (e) => {
    const { name, value } = e.target;
    setNewIngredient({
      ...newIngredient,
      [name]: value
    });
  };

  // Handle input change for editing ingredient
  const handleEditIngredientChange = (e) => {
    const { name, value } = e.target;
    setEditingIngredient({
      ...editingIngredient,
      [name]: value
    });
  };

  // Handle adding a new ingredient
  const handleAddIngredient = async (e) => {
    e.preventDefault();
    
    // Comprehensive validation - check all required fields
    const requiredFields = [
      { field: 'code', label: 'Code' },
      { field: 'tradeName', label: 'Trade Name' },
      { field: 'purpose', label: 'Purpose' },
      { field: 'ingredientName', label: 'Ingredient Name' },
      { field: 'casNumber', label: 'CAS Number' },
      { field: 'specificGravity', label: 'Specific Gravity' }
    ];
    
    const emptyFields = requiredFields.filter(({ field }) => {
      const value = newIngredient[field];
      return !value || (typeof value === 'string' && value.trim() === '');
    });
    
    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.map(({ label }) => label).join(', ');
      setError(`The following fields are required: ${fieldNames}`);
      return;
    }
    
    // Validate specific gravity is a positive number
    const specificGravity = parseFloat(newIngredient.specificGravity);
    if (isNaN(specificGravity) || specificGravity <= 0) {
      setError('Specific Gravity must be a positive number');
      return;
    }
    
    // Validate additive fraction is between 0 and 1 (stored as fraction)
    const percentInAdditive = parseFloat(newIngredient.percentInAdditive);
    if (isNaN(percentInAdditive) || percentInAdditive < 0 || percentInAdditive > 1) {
      setError('Additive fraction must be between 0 and 1 (e.g., 0.5 for 50%)');
      return;
    }
    
    try {
      setLoading(true);
      await addIngredient(newIngredient);
      
      // Refresh the entire ingredients list from the server
      const refreshedIngredients = await getIngredients();
      setIngredients(refreshedIngredients);
      
      setNewIngredient({
        code: '',
        tradeName: '',
        vendorId: '',
        purpose: '',
        ingredientName: '',
        casNumber: '',
        percentInAdditive: 1, // Reset to 1 (100% as fraction)
        percentInHFJob: 0,
        massIngredient: 0,
        units: 'gal',
        specificGravity: ''
      });
      setError('');
    } catch (error) {
      console.error('Error adding ingredient:', error);
      setError('Failed to add ingredient. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle updating an ingredient
  const handleUpdateIngredient = async (e) => {
    e.preventDefault();
    
    // Comprehensive validation - check all required fields
    const requiredFields = [
      { field: 'code', label: 'Code' },
      { field: 'tradeName', label: 'Trade Name' },
      { field: 'purpose', label: 'Purpose' },
      { field: 'ingredientName', label: 'Ingredient Name' },
      { field: 'casNumber', label: 'CAS Number' },
      { field: 'specificGravity', label: 'Specific Gravity' }
    ];
    
    const emptyFields = requiredFields.filter(({ field }) => {
      const value = editingIngredient[field];
      return !value || (typeof value === 'string' && value.trim() === '');
    });
    
    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.map(({ label }) => label).join(', ');
      setError(`The following fields are required: ${fieldNames}`);
      return;
    }
    
    // Validate specific gravity is a positive number
    const specificGravity = parseFloat(editingIngredient.specificGravity);
    if (isNaN(specificGravity) || specificGravity <= 0) {
      setError('Specific Gravity must be a positive number');
      return;
    }
    
    // Validate additive fraction is between 0 and 1 (stored as fraction)
    const percentInAdditive = parseFloat(editingIngredient.percentInAdditive);
    if (isNaN(percentInAdditive) || percentInAdditive < 0 || percentInAdditive > 1) {
      setError('Additive fraction must be between 0 and 1 (e.g., 0.5 for 50%)');
      return;
    }
    
    try {
      setLoading(true);
      await updateIngredient(editingIngredient.code, editingIngredient);
      
      // Refresh the entire ingredients list from the server
      const refreshedIngredients = await getIngredients();
      setIngredients(refreshedIngredients);
      
      setEditingIngredient(null);
      setError('');
    } catch (error) {
      console.error('Error updating ingredient:', error);
      setError('Failed to update ingredient. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting an ingredient
  const handleDeleteIngredient = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ingredient?')) {
      return;
    }
    
    try {
      setLoading(true);
      await deleteIngredient(id);
      setIngredients(ingredients.filter(ing => ing.id !== id));
      setError('');
    } catch (error) {
      console.error('Error deleting ingredient:', error);
      setError('Failed to delete ingredient. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter ingredients based on search term
  const filteredIngredients = ingredients.filter(ingredient => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (ingredient.code && ingredient.code.toLowerCase().includes(searchLower)) ||
      (ingredient.tradeName && ingredient.tradeName.toLowerCase().includes(searchLower)) ||
      (ingredient.ingredientName && ingredient.ingredientName.toLowerCase().includes(searchLower)) ||
      (ingredient.supplier && String(ingredient.supplier).toLowerCase().includes(searchLower)) ||
      (ingredient.casNumber && ingredient.casNumber.toLowerCase().includes(searchLower)) ||
      (ingredient.purpose && ingredient.purpose.toLowerCase().includes(searchLower))
    );
  });

  // Sort ingredients based on sort field and direction
  const sortedIngredients = [...filteredIngredients].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // Handle numeric fields
    if (['percentInAdditive', 'percentInHFJob', 'massIngredient'].includes(sortField)) {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    } else {
      // String comparison
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '▲' : '▼';
  };

  return (
    <div className="ingredients-management">
      <div className="ingredients-management-header">
        <h2>Ingredients Management</h2>
        <p>Manage the ingredients table for FracFocus disclosure</p>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="ingredients-controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search ingredients..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
      </div>
      
      {loading ? (
        <div className="loading-message">Loading ingredients...</div>
      ) : (
        <>
          <div className="ingredients-table-container">
            <table className="ingredients-table">
              <thead>
                <tr>
                  <th onClick={() => handleSortChange('code')}>
                    Code {getSortIndicator('code')}
                  </th>
                  <th onClick={() => handleSortChange('tradeName')}>
                    Trade Name {getSortIndicator('tradeName')}
                  </th>
                  <th onClick={() => handleSortChange('supplier')}>
                    Supplier {getSortIndicator('supplier')}
                  </th>
                  <th onClick={() => handleSortChange('purpose')}>
                    Purpose {getSortIndicator('purpose')}
                  </th>
                  <th onClick={() => handleSortChange('ingredientName')}>
                    Ingredient Name {getSortIndicator('ingredientName')}
                  </th>
                  <th onClick={() => handleSortChange('casNumber')}>
                    CAS Number {getSortIndicator('casNumber')}
                  </th>
                  <th onClick={() => handleSortChange('percentInAdditive')}>
                    Additive Fraction {getSortIndicator('percentInAdditive')}
                  </th>
                  <th onClick={() => handleSortChange('units')}>
                    Units {getSortIndicator('units')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedIngredients.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="no-ingredients">
                      No ingredients found. Add some below.
                    </td>
                  </tr>
                ) : (
                  sortedIngredients.map((ingredient) => (
                    <tr key={ingredient.id || ingredient.code}>
                      <td>{ingredient.code}</td>
                      <td>{ingredient.tradeName}</td>
                      <td>{getSupplierDisplayName(ingredient.supplier)}</td>
                      <td>{ingredient.purpose}</td>
                      <td>{ingredient.ingredientName}</td>
                      <td>{ingredient.casNumber}</td>
                      <td>{ingredient.percentInAdditive}</td>
                      <td>{ingredient.units}</td>
                      <td className="action-buttons">
                        <button 
                          className="edit-button"
                          onClick={() => setEditingIngredient(ingredient)}
                          title="Edit Ingredient"
                        >
                          Edit
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDeleteIngredient(ingredient.id || ingredient.code)}
                          title="Delete Ingredient"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          <div className="add-ingredient-form">
            <h3>Add New Ingredient</h3>
            <form onSubmit={handleAddIngredient}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="code">Code *</label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={newIngredient.code}
                    onChange={handleNewIngredientChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="tradeName">Trade Name *</label>
                  <input
                    type="text"
                    id="tradeName"
                    name="tradeName"
                    value={newIngredient.tradeName}
                    onChange={handleNewIngredientChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="vendorId">Supplier</label>
                  <select
                    id="vendorId"
                    name="vendorId"
                    value={newIngredient.vendorId}
                    onChange={handleNewIngredientChange}
                  >
                    <option value="">Select a supplier</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.company} ({vendor.first_name} {vendor.last_name})
                      </option>
                    ))}
                  </select>
                  <small>Required for proprietary CAS numbers</small>
                </div>
                <div className="form-group">
                  <label htmlFor="purpose">Purpose *</label>
                  <input
                    type="text"
                    id="purpose"
                    name="purpose"
                    value={newIngredient.purpose}
                    onChange={handleNewIngredientChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="casNumber">CAS Number *</label>
                  <input
                    type="text"
                    id="casNumber"
                    name="casNumber"
                    value={newIngredient.casNumber}
                    onChange={handleNewIngredientChange}
                    placeholder="XX-XX-X"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ingredientName">Ingredient Name *</label>
                  <input
                    type="text"
                    id="ingredientName"
                    name="ingredientName"
                    value={newIngredient.ingredientName}
                    onChange={handleNewIngredientChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="percentInAdditive">Additive Fraction (0-1)</label>
                  <input
                    type="number"
                    id="percentInAdditive"
                    name="percentInAdditive"
                    value={newIngredient.percentInAdditive}
                    onChange={handleNewIngredientChange}
                    min="0"
                    max="1"
                    step="0.001"
                    placeholder="1 = 100%"
                  />
                  <small>Enter as fraction (e.g., 0.5 for 50%)</small>
                </div>
                <div className="form-group">
                  <label htmlFor="units">Units</label>
                  <select
                    id="units"
                    name="units"
                    value={newIngredient.units}
                    onChange={handleNewIngredientChange}
                  >
                    <option value="gal">gal</option>
                    <option value="lbs">lbs</option>
                    <option value="kg">kg</option>
                    <option value="L">L</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="specificGravity">Specific Gravity</label>
                  <input
                    type="number"
                    id="specificGravity"
                    name="specificGravity"
                    value={newIngredient.specificGravity}
                    onChange={handleNewIngredientChange}
                    min="0.1"
                    max="10"
                    step="0.01"
                    placeholder="1.0"
                  />
                </div>
              </div>
              
              
              <div className="form-actions">
                <button type="submit" className="add-button">
                  Add Ingredient
                </button>
              </div>
            </form>
          </div>
        </>
      )}
      
      {editingIngredient && (
        <div className="edit-ingredient-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Ingredient</h3>
              <button 
                className="close-button"
                onClick={() => setEditingIngredient(null)}
              >
                &times;
              </button>
            </div>
            
            <form onSubmit={handleUpdateIngredient}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-code">Code *</label>
                  <input
                    type="text"
                    id="edit-code"
                    name="code"
                    value={editingIngredient.code}
                    onChange={handleEditIngredientChange}
                    required
                    disabled
                  />
                  <small>Code cannot be changed</small>
                </div>
                <div className="form-group">
                  <label htmlFor="edit-tradeName">Trade Name *</label>
                  <input
                    type="text"
                    id="edit-tradeName"
                    name="tradeName"
                    value={editingIngredient.tradeName || ''}
                    onChange={handleEditIngredientChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-vendorId">Supplier</label>
                  <select
                    id="edit-vendorId"
                    name="vendorId"
                    value={editingIngredient.vendorId || ''}
                    onChange={handleEditIngredientChange}
                  >
                    <option value="">Select a supplier</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.company} ({vendor.first_name} {vendor.last_name})
                      </option>
                    ))}
                  </select>
                  <small>Required for proprietary CAS numbers</small>
                </div>
                <div className="form-group">
                  <label htmlFor="edit-purpose">Purpose *</label>
                  <input
                    type="text"
                    id="edit-purpose"
                    name="purpose"
                    value={editingIngredient.purpose || ''}
                    onChange={handleEditIngredientChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-casNumber">CAS Number *</label>
                  <input
                    type="text"
                    id="edit-casNumber"
                    name="casNumber"
                    value={editingIngredient.casNumber || ''}
                    onChange={handleEditIngredientChange}
                    placeholder="XX-XX-X"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-ingredientName">Ingredient Name *</label>
                  <input
                    type="text"
                    id="edit-ingredientName"
                    name="ingredientName"
                    value={editingIngredient.ingredientName || ''}
                    onChange={handleEditIngredientChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-percentInAdditive">Additive Fraction (0-1)</label>
                  <input
                    type="number"
                    id="edit-percentInAdditive"
                    name="percentInAdditive"
                    value={editingIngredient.percentInAdditive || 0}
                    onChange={handleEditIngredientChange}
                    min="0"
                    max="1"
                    step="0.001"
                    placeholder="1 = 100%"
                  />
                  <small>Enter as fraction (e.g., 0.5 for 50%)</small>
                </div>
                <div className="form-group">
                  <label htmlFor="edit-units">Units</label>
                  <select
                    id="edit-units"
                    name="units"
                    value={editingIngredient.units || 'gal'}
                    onChange={handleEditIngredientChange}
                  >
                    <option value="gal">gal</option>
                    <option value="lbs">lbs</option>
                    <option value="kg">kg</option>
                    <option value="L">L</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-specificGravity">Specific Gravity *</label>
                  <input
                    type="number"
                    id="edit-specificGravity"
                    name="specificGravity"
                    value={editingIngredient.specificGravity || ''}
                    onChange={handleEditIngredientChange}
                    min="0.1"
                    max="10"
                    step="0.01"
                    placeholder="1.0"
                    required
                  />
                </div>
              </div>
              
              
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={() => setEditingIngredient(null)}>
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IngredientsManagement;
